export type ModifierGroupStacking = 'exclusive' | 'stack' | 'repeatable';

export type ModifierGroupKind = 'modifier' | 'variant';

export interface SelectedModifier {
  id: string;
  name: string;
  priceDeltaCents: number;
  priceOverrideCents: number | null;
  quantity: number;
  allergenTags: string[];
}

export interface SelectedModifierGroup {
  id: string;
  name: string;
  kind: ModifierGroupKind;
  stackingBehavior: ModifierGroupStacking;
  selectionMode: 'single' | 'multiple';
  allowQuantity: boolean;
  selections: SelectedModifier[];
}

export interface CalculateItemTotalParams {
  basePriceCents: number;
  groups: SelectedModifierGroup[];
}

export interface CalculateItemTotalResult {
  unitPriceCents: number;
  modifiersTotalCents: number;
  basePriceCents: number;
}

const clampCurrency = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.round(value));
};

const resolveSelectionQuantity = (
  group: SelectedModifierGroup,
  selection: SelectedModifier,
): number => {
  if (group.stackingBehavior === 'repeatable' && group.allowQuantity) {
    return Math.max(1, Math.round(selection.quantity));
  }
  return 1;
};

const resolveModifierDelta = (selection: SelectedModifier): number => {
  if (selection.priceOverrideCents !== null && selection.priceOverrideCents !== undefined) {
    return selection.priceOverrideCents;
  }
  return selection.priceDeltaCents ?? 0;
};

const applyVariantOverride = (
  basePriceCents: number,
  group: SelectedModifierGroup,
): number => {
  if (group.selections.length === 0) {
    return basePriceCents;
  }

  let resolved = basePriceCents;

  for (const selection of group.selections) {
    const override =
      selection.priceOverrideCents !== null && selection.priceOverrideCents !== undefined
        ? selection.priceOverrideCents
        : basePriceCents + (selection.priceDeltaCents ?? 0);

    resolved = override;

    if (group.stackingBehavior === 'exclusive') {
      break;
    }
  }

  return resolved;
};

export const calculateItemTotal = ({
  basePriceCents,
  groups,
}: CalculateItemTotalParams): CalculateItemTotalResult => {
  let resolvedBase = clampCurrency(basePriceCents);
  let modifiersTotal = 0;

  for (const group of groups) {
    if (group.kind === 'variant') {
      resolvedBase = clampCurrency(applyVariantOverride(resolvedBase, group));
      continue;
    }

    for (const selection of group.selections) {
      const quantity = resolveSelectionQuantity(group, selection);
      const delta = resolveModifierDelta(selection);
      modifiersTotal += clampCurrency(delta) * quantity;
    }
  }

  const unitPriceCents = clampCurrency(resolvedBase + modifiersTotal);

  return {
    unitPriceCents,
    modifiersTotalCents: clampCurrency(modifiersTotal),
    basePriceCents: resolvedBase,
  };
};

